/**
 * @file countdown.ts
 * @brief A scheduled session's countdown, one format and one ticking hook
 *        for every web card (Home's Scheduled rail, the Diary).
 *
 * @details
 * The phone's twin is FRONTEND/src/shared/utils/countdown.ts (DES
 * 04-10-2026): "1d 03h", "1:59:12", or "45:12" under an hour. Seconds show
 * under a day so the FlipClock beside a clock icon visibly runs; no
 * "Starts in" prefix.
 *
 * @author DES
 * @date 04-10-2026
 */

import { useEffect, useState } from "react";

const pad = (n: number) => String(n).padStart(2, "0");

/** `ms` until the moment; null when it has passed. */
export function formatCountdown(ms: number): string | null {
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const t = Math.floor(ms / 1000);
  const d = Math.floor(t / 86400);
  const h = Math.floor((t % 86400) / 3600);
  const m = Math.floor((t % 3600) / 60);
  if (d > 0) return `${d}d ${pad(h)}h`;
  if (h > 0) return `${h}:${pad(m)}:${pad(t % 60)}`;
  return `${m}:${pad(t % 60)}`;
}

/** The phone's 90-minute join window after the start. */
export const JOIN_WINDOW_MS = 90 * 60 * 1000;

/**
 * Countdown to `iso`; with `throughJoinWindow`, then on through the join
 * window. Null when inactive or over. Ticks every second while active.
 */
export function useCountdown(
  iso: string | null | undefined,
  active: boolean,
  throughJoinWindow = false,
): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  const start = iso ? new Date(iso).getTime() : NaN;
  if (!active || Number.isNaN(start)) return null;
  if (now < start) return formatCountdown(start - now);
  return throughJoinWindow ? formatCountdown(start + JOIN_WINDOW_MS - now) : null;
}
